import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { getAdminData, putAdminData } from "../../apis";
import { adminEventProductsPath } from "../../apis/config";
import {
  AdminActiveEvent,
  AdminEventProductRow,
} from "../../interfaces/AdminEventProduct";
import {
  downloadEventProductsExcel,
  parseEventProductsExcel,
} from "../../utils/eventProductExcel";
import {
  activeEventPortalPath,
  portalAbsoluteUrl,
} from "../../utils/eventPortalLink";
import { createPortalQrDataUrl } from "../../utils/portalQr";
import { saveAs } from "file-saver";

type EventProductExcelPanelProps = {
  active: AdminActiveEvent | null;
  loadingActive: boolean;
};

export default function EventProductExcelPanel({
  active,
  loadingActive,
}: EventProductExcelPanelProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [qrModal, setQrModal] = useState<{
    dataUrl: string;
    url: string;
  } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const portalHref =
    active != null &&
    active.type != null &&
    active.urlCode != null &&
    String(active.urlCode).trim() !== ""
      ? activeEventPortalPath(active.type, String(active.urlCode))
      : null;

  useEffect(() => {
    if (!qrModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setQrModal(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [qrModal]);

  const handleQrOpen = async () => {
    if (!portalHref || !active) return;
    setBusy(true);
    setError(null);
    try {
      const abs = portalAbsoluteUrl(portalHref);
      const dataUrl = await createPortalQrDataUrl(abs);
      setQrModal({ dataUrl, url: abs });
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "QR 이미지를 만들지 못했습니다.",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleQrDownloadPng = async () => {
    if (!qrModal || !active) return;
    const res = await fetch(qrModal.dataUrl);
    const blob = await res.blob();
    saveAs(blob, `qr-portal-${active.id}.png`);
  };

  const handleDownload = async () => {
    if (!active) return;
    setError(null);
    setMessage(null);
    setBusy(true);
    try {
      const res = await getAdminData<AdminEventProductRow[]>(
        adminEventProductsPath(active.id),
      );
      if (!res.isSuccess || !Array.isArray(res.result)) {
        setError(res.message || "상품 목록을 불러오지 못했습니다.");
        return;
      }
      const normalized: AdminEventProductRow[] =
        res.result.map(normalizeProductRow);
      downloadEventProductsExcel(normalized, `행사상품_${active.name}`);
      setMessage("엑셀 파일을 내려받았습니다.");
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "다운로드 중 오류가 발생했습니다.",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !active) return;
    setError(null);
    setMessage(null);
    setBusy(true);
    try {
      const products = await parseEventProductsExcel(file);
      const res = await putAdminData<unknown>(
        adminEventProductsPath(active.id),
        { products },
      );
      if (!res.isSuccess) {
        setError(res.message || "업로드 반영에 실패했습니다.");
        return;
      }
      setMessage(
        `업로드한 ${products.length}건으로 상품 데이터를 덮어썼습니다.`,
      );
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "업로드 처리 중 오류가 발생했습니다.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm text-left">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-semibold text-slate-900">
          행사 판매 상품 &gt; 엑셀 업로드
        </h3>
        <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
          {active &&
            (portalHref ? (
              <>
                <a
                  href={portalHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
                >
                  해당 사이트로 이동
                </a>
                <button
                  type="button"
                  onClick={() => void handleQrOpen()}
                  disabled={busy}
                  className="inline-flex items-center rounded-md border border-violet-500 bg-white px-3 py-1.5 text-sm font-medium text-violet-800 hover:bg-violet-50 disabled:opacity-50"
                >
                  QR 이미지
                </button>
              </>
            ) : (
              <button
                type="button"
                disabled
                title="활성 행사에 유형·URL 코드가 있어야 합니다."
                className="rounded-md border border-slate-200 bg-slate-100 px-3 py-1.5 text-sm text-slate-400 cursor-not-allowed"
              >
                해당 사이트로 이동
              </button>
            ))}
        </div>
      </div>

      <p className="mt-4 text-sm text-slate-500">
        상품코드, 위치, 메모, 상품명, 체험여부, 표시유무 (반드시 Y 또는 N. 빈
        칸은 미표시로 처리).
      </p>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <button
          type="button"
          onClick={() => void handleDownload()}
          disabled={busy || !active || loadingActive}
          className="rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
        >
          엑셀 다운로드
        </button>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => void handleFileChange(e)}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy || !active || loadingActive}
            className="rounded-md border border-slate-400 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            엑셀 업로드 (덮어쓰기)
          </button>
        </div>
      </div>

      {error && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      {message && (
        <p className="mt-3 text-sm text-emerald-700" role="status">
          {message}
        </p>
      )}

      {qrModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="qr-modal-title"
          onClick={() => setQrModal(null)}
        >
          <div
            className="max-w-md rounded-lg bg-white p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h4
              id="qr-modal-title"
              className="text-lg font-semibold text-slate-900 mb-3"
            >
              행사 판매 상품 페이지 QR
            </h4>
            <div className="flex justify-center bg-white">
              <img
                src={qrModal.dataUrl}
                alt="포털 주소 QR 코드"
                className="rounded border border-slate-200 max-w-full h-auto"
              />
            </div>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => void handleQrDownloadPng()}
                className="rounded-md bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-900"
              >
                PNG 저장
              </button>
              <button
                type="button"
                onClick={() => setQrModal(null)}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-50"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function sanitizeFilePart(name: string): string {
  return name.replace(/[^\w\-가-힣]+/g, "_").slice(0, 40) || "event";
}

function normalizeProductRow(r: unknown): AdminEventProductRow {
  const o = r as Record<string, unknown>;
  return {
    productCode: String(o.productCode ?? ""),
    location: String(o.location ?? ""),
    note: String(o.note ?? ""),
    product: String(o.product ?? ""),
    demoInfo: String(o.demoInfo ?? ""),
    isSearchable: Boolean(o.isSearchable),
  };
}
